import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import {
  Users, UserPlus, Edit3, Trash2, Loader2, Shield, BookOpen,
  Key, Mail, Phone, Check, X, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const TeamManagement = () => {
  const [executives, setExecutives] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    assigned_course_ids: [],
  });

  const fetchData = useCallback(async () => {
    try {
      const [tRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/team/content-executives`, { headers: authHdr() }),
        axios.get(`${API}/admin/courses`, { headers: authHdr() }),
      ]);
      setExecutives(tRes.data || []);
      setCourses(cRes.data || []);
    } catch (err) { toast.error('Failed to load team'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', phone: '', assigned_course_ids: [] });
    setDialog(true);
  };

  const openEdit = (ex) => {
    setEditing(ex);
    setForm({
      name: ex.name, email: ex.email, password: '', phone: ex.phone || '',
      assigned_course_ids: ex.assigned_course_ids || [],
    });
    setDialog(true);
  };

  const toggleCourse = (cid) => {
    setForm(f => {
      const has = f.assigned_course_ids.includes(cid);
      return {
        ...f,
        assigned_course_ids: has
          ? f.assigned_course_ids.filter(x => x !== cid)
          : [...f.assigned_course_ids, cid],
      };
    });
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email required'); return;
    }
    if (!editing && (!form.password || form.password.length < 6)) {
      toast.error('Password must be at least 6 characters'); return;
    }
    try {
      if (editing) {
        const body = { ...form };
        if (!body.password) delete body.password;
        await axios.put(`${API}/admin/team/content-executives/${editing.user_id}`,
          body, { headers: authHdr() });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/team/content-executives`, form, { headers: authHdr() });
        toast.success(`Created — ${form.name} can login at /login`);
      }
      setDialog(false);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };

  const toggleActive = async (ex) => {
    try {
      await axios.put(`${API}/admin/team/content-executives/${ex.user_id}`,
        { active: !ex.active }, { headers: authHdr() });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this content executive? They will lose access immediately.')) return;
    try {
      await axios.delete(`${API}/admin/team/content-executives/${id}`, { headers: authHdr() });
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="team-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-orange-500" /> Team — Content Executives
          </h2>
          <p className="text-sm text-muted-foreground">
            Create content executives who can upload lessons + assignments to assigned courses only.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openNew} className="bg-gradient-to-r from-orange-500 to-red-500" data-testid="add-ce-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add Content Executive
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-bold">{executives.length}</p></div>
          <Users className="w-5 h-5 text-blue-500" />
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-xs text-muted-foreground">Active</p><p className="text-2xl font-bold">{executives.filter(e => e.active).length}</p></div>
          <Shield className="w-5 h-5 text-emerald-500" />
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-xs text-muted-foreground">Assigned Courses</p><p className="text-2xl font-bold">
            {new Set(executives.flatMap(e => e.assigned_course_ids || [])).size}
          </p></div>
          <BookOpen className="w-5 h-5 text-purple-500" />
        </CardContent></Card>
      </div>

      {/* List */}
      <div className="space-y-2">
        {executives.length === 0 && (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
            No content executives yet. Click <strong>Add Content Executive</strong> to onboard one.
          </CardContent></Card>
        )}
        {executives.map(ex => (
          <Card key={ex.user_id} data-testid={`ce-${ex.user_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white font-bold flex items-center justify-center shrink-0">
                    {ex.name?.[0]?.toUpperCase() || 'C'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{ex.name}</h3>
                      <Badge variant={ex.active ? 'default' : 'outline'} className="text-[10px]">
                        {ex.active ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                    <div className="flex gap-3 flex-wrap text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{ex.email}</span>
                      {ex.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{ex.phone}</span>}
                    </div>
                    <div className="flex gap-1 flex-wrap mt-2">
                      {(ex.assigned_courses || []).map(ac => (
                        <Badge key={ac.course_id} variant="outline" className="text-[10px]">
                          <BookOpen className="w-3 h-3 mr-1" />{ac.title}
                        </Badge>
                      ))}
                      {(!ex.assigned_courses || ex.assigned_courses.length === 0) && (
                        <span className="text-[10px] text-muted-foreground italic">No courses assigned</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => toggleActive(ex)}
                    title={ex.active ? 'Disable' : 'Enable'}>
                    {ex.active ? <X className="w-4 h-4 text-amber-500" /> : <Check className="w-4 h-4 text-emerald-500" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(ex)} title="Edit">
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(ex.user_id)} title="Delete">
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Content Executive' : 'Onboard New Content Executive'}</DialogTitle>
            <DialogDescription>
              They'll login at <code className="text-xs bg-muted px-1">/login</code> and only see their assigned courses.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name *</Label><Input value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} data-testid="form-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} disabled={!!editing}
              onChange={e => setForm({ ...form, email: e.target.value })} data-testid="form-email" /></div>
            <div>
              <Label>Password {editing && <span className="text-xs text-muted-foreground">(leave blank to keep current)</span>}</Label>
              <Input type="password" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? 'Change password...' : 'Min 6 characters'} data-testid="form-password" />
            </div>
            <div><Label>Phone (optional)</Label><Input value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <Label>Assign Courses ({form.assigned_course_ids.length} selected)</Label>
              <div className="space-y-2 mt-2 max-h-60 overflow-y-auto border rounded-md p-3">
                {courses.length === 0 && (
                  <p className="text-xs text-muted-foreground">No courses yet. Create courses first.</p>
                )}
                {courses.map(c => (
                  <label key={c.course_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded"
                    data-testid={`course-check-${c.course_id}`}>
                    <input type="checkbox" checked={form.assigned_course_ids.includes(c.course_id)}
                      onChange={() => toggleCourse(c.course_id)} />
                    <span className="flex-1">{c.title}</span>
                    <Badge variant="outline" className="text-[10px]">{c.category}</Badge>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={save} data-testid="form-save">
              {editing ? 'Save Changes' : 'Create & Send Credentials'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
